import { Nullable } from '../../utils/customTypes';
import {
  FieldsDuplicationInputData,
  FieldsDuplicationOutputData,
  LinkConfigurationInputData,
  LinkConfigurationsInputSelectData,
  LinkConfigurationsOutputData,
  LinkConfigurationSelectOutputData,
  MerchantConfigurationsInputData,
  MerchantConfigurationsInputSelectData,
  MerchantConfigurationsOutputData,
  MerchantConfigurationsOutputSelectData,
  MerchantInputData,
  MerchantOutputData,
  OpenParserInputData,
  OpenParserOutputData,
  PaymentRequestInputData,
  PaymentRequestOutputData,
  TemplateInputType,
  TemplateOutputType,
} from './openTypes';

const parseMerchant: (merchantData: MerchantInputData) => MerchantOutputData = (
  merchantData
) => {
  const { id, name, logo, colors, preferred_template } = merchantData;
  const parsedMerchant = {
    id,
    name,
    logo,
    preferredTemplate: preferred_template,
    primaryColor: null,
    secondaryColor: null,
  };
  if (colors !== null) {
    const parsedColors = JSON.parse(colors);
    return {
      ...parsedMerchant,
      primaryColor: parsedColors.primaryColor,
      secondaryColor: parsedColors.secondaryColor,
    };
  }
  return parsedMerchant;
};

const parseLinkConfiguration: (
  linkConfigurationData: LinkConfigurationInputData
) => LinkConfigurationsOutputData = (linkConfigurationData) => {
  const {
    id,
    custom_field_type: customFieldType,
    position,
    hidden,
    label,
    dismissable,
    value,
    name,
    placeholder,
    validations,
    data,
  } = linkConfigurationData;

  const linkConfigurationsSelectTypes = [
    'LinkConfigurations::ChangingOptionsSelect',
  ];

  let parsedData = null;

  if (data === null) {
    parsedData = null;
  } else if (
    linkConfigurationsSelectTypes.includes(customFieldType) &&
    typeof data !== 'string'
  ) {
    parsedData = data.map((dat) =>
      parseMerchantConfigurationsSelectTypesData(dat)
    );
  } else if (typeof data === 'string') {
    parsedData = JSON.parse(data);
  }

  return {
    id,
    customFieldType,
    position,
    hidden,
    label,
    dismissable,
    value,
    name,
    placeholder,
    validations,
    data: parsedData,
  };
};

const parseLinkConfigurationsSelectTypesData: (
  data: LinkConfigurationsInputSelectData
) => LinkConfigurationSelectOutputData = (data) => {
  const { config, is_clearable, multiple, selected, value } = data;
  return {
    config,
    isClearable: is_clearable,
    multiple,
    selected,
    value,
  };
};

const parseMerchantConfigurationsSelectTypesData: (
  data: MerchantConfigurationsInputSelectData
) => MerchantConfigurationsOutputSelectData = (data) => {
  const { config, is_clearable, multiple, selected, value } = data;
  return {
    config,
    isClearable: is_clearable,
    multiple,
    selected,
    value,
  };
};

const parseMerchantConfigurations: (
  merchantConfigurationsData: MerchantConfigurationsInputData
) => MerchantConfigurationsOutputData = (merchantConfigurationsData) => {
  const {
    id,
    custom_field_type: configurationType,
    position,
    hidden,
    label,
    dismissable,
    value,
    name,
    placeholder,
    validations,
    data,
  } = merchantConfigurationsData;

  const merchantConfigurationsSelectTypes = [
    'MerchantConfigurations::Fields::PaymentMethodCommissions',
  ];

  let parsedData = null;

  if (data === null) {
    parsedData = null;
  } else if (
    merchantConfigurationsSelectTypes.includes(configurationType) &&
    typeof data !== 'string'
  ) {
    parsedData = data.map((dat) => parseLinkConfigurationsSelectTypesData(dat));
  } else if (typeof data === 'string') {
    parsedData = JSON.parse(data);
  }

  return {
    id,
    configurationType,
    position,
    hidden,
    label,
    dismissable,
    value,
    name,
    placeholder,
    validations,
    data: parsedData,
  };
};

const parsePaymentRequest: (
  paymentRequestData: PaymentRequestInputData
) => PaymentRequestOutputData = (paymentRequestData) => {
  const {
    id,
    identifier,
    installments,
    name,
    email,
    amount,
    currency,
    blank_amount: blankAmount,
    has_iva: hasIva,
    has_link_configurations: hasLinkConfigurations,
    link_configurations: linkConfigurationsData,
    merchant_configurations: merchantConfigurationsData,
    header,
  } = paymentRequestData;

  return {
    id,
    identifier,
    name,
    email,
    amount,
    currency,
    blankAmount,
    hasIva,
    hasLinkConfigurations,
    installments,
    linkConfigurations: linkConfigurationsData
      .map((linkConfigurationData) =>
        parseLinkConfiguration(linkConfigurationData)
      )
      .sort((a, b) => a.position - b.position),
    merchantConfigurations: (merchantConfigurationsData || []).map(
      (merchantConfigurationData) =>
        parseMerchantConfigurations(merchantConfigurationData)
    ),
    headerText: header,
  };
};

export const templateParser: (data: TemplateInputType) => TemplateOutputType = (
  data: TemplateInputType
) => {
  const {
    data: { merchant: merchantData, valid },
  } = data;

  return {
    merchant: parseMerchant(merchantData),
    valid,
  };
};

const duplicateFieldsParser: (
  input: Nullable<FieldsDuplicationInputData>
) => FieldsDuplicationOutputData = (input) => {
  if (!input) {
    return {
      duplicate: false,
      paymentDuplication: {
        allow: false,
        maxDuplication: 0,
      },
      displayName: null,
      fields: [],
      maxDuplication: 3,
    };
  }

  const { data, duplicate, payment_duplication: paymentDuplication } = input;

  return {
    duplicate: duplicate,
    paymentDuplication: {
      allow: paymentDuplication.allow,
      maxDuplication: paymentDuplication.max_duplication,
    },
    displayName: data?.display_name || null,
    fields: data?.fields || [],
    maxDuplication: data?.max_duplication || 3,
  };
};

const openParser: (data: OpenParserInputData) => OpenParserOutputData = (
  data
) => {
  const {
    data: {
      merchant: merchantData,
      payment_request: paymentRequestData,
      subscription,
      generated_link: generatedLink,
      metrics_token: metricsToken,
      fields_duplication: fieldsDuplication,
      storable,
    },
  } = data;

  const parsedData = {
    merchant: parseMerchant(merchantData),
    paymentRequest: parsePaymentRequest(paymentRequestData),
    isSubscription: subscription,
    fieldsDuplication: duplicateFieldsParser(fieldsDuplication),
    generatedLink,
    metricsToken,
    storable,
  };

  return parsedData;
};

export default openParser;
